/*
 © Copyright 2026 Daikin Industries, Ltd. All rights reserved.

 This software is the proprietary information of Daikin Industries, Ltd.
 By using this software, you agree to the following terms:

 1. You are granted a revocable non-exclusive, non-transferable license to use this software.
 2. You agree not to modify, distribute, or create derivative works based on this software.
 3. Unauthorized copying, distribution, or use of any portion of this software is strictly prohibited.
 4. This software is protected by copyright laws and international treaties.
 5. Any violation of these terms will result in the termination of your license and may result in legal action.
 6. This is Daikin's proprietary software and may not be used in an unauthorized manner or without express authorization from Daikin.
*/

import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  SwaggerState,
  DEFAULT_SWAGGER_STATE,
  OpenAPISpec,
  SwaggerRequest,
  PendingRequest
} from './models/swagger-state.model';
import { OAuthConfig, DEFAULT_OAUTH_CONFIG } from './models/oauth-config.model';

/**
 * Swagger State Management Service
 *
 * Centralized state management for Swagger UI integration.
 * Replaces global window object usage with proper Angular service.
 *
 * Benefits:
 * - Type-safe state management
 * - Prevents memory leaks
 * - Enables multiple Swagger UI instances
 * - Testable and maintainable
 * - RxJS-based reactive state
 */
@Injectable({
  providedIn: 'root'
})
export class SwaggerStateService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  /**
   * Internal state subject - immutable state updates only
   */
  private readonly stateSubject = new BehaviorSubject<SwaggerState>(
    this.createInitialState()
  );

  /**
   * Public observable for state changes
   * Components can subscribe to this for reactive updates
   */
  readonly state$: Observable<SwaggerState> = this.stateSubject
    .asObservable()
    .pipe(takeUntil(this.destroy$));

  /**
   * Get current state snapshot (non-reactive)
   */
  get currentState(): Readonly<SwaggerState> {
    return this.stateSubject.getValue();
  }

  // ============================================================================
  // Authentication Token Management
  // ============================================================================

  /**
   * Set authentication token
   * @param token JWT access token or null to clear
   */
  setAuthToken(token: string | null): void {
    this.updateState({ authToken: token });
  }

  /**
   * Get current authentication token
   * @returns Current token or null
   */
  getAuthToken(): string | null {
    return this.currentState.authToken;
  }

  /**
   * Check if authentication token is available
   */
  hasAuthToken(): boolean {
    return !!this.currentState.authToken;
  }

  // ============================================================================
  // OpenAPI Spec Management
  // ============================================================================

  /**
   * Set OpenAPI specification
   * @param spec OpenAPI/Swagger specification object
   */
  setSwaggerSpec(spec: OpenAPISpec | null): void {
    this.updateState({ swaggerSpec: spec });
  }

  /**
   * Get current OpenAPI specification
   */
  getSwaggerSpec(): OpenAPISpec | null {
    return this.currentState.swaggerSpec;
  }

  /**
   * Extracts OAuth configuration from the current OpenAPI spec
   * @returns OAuth configuration with clientId and scopes
   */
  getOAuthConfig(): OAuthConfig {
    const spec = this.currentState.swaggerSpec;

    if (!spec) {
      return DEFAULT_OAUTH_CONFIG;
    }

    try {
      const securitySchemes = spec?.components?.securitySchemes;
      if (!securitySchemes) {
        return DEFAULT_OAUTH_CONFIG;
      }

      // Find OAuth2 security scheme
      const oauth2Scheme = Object.values(securitySchemes).find(
        (scheme: any) => scheme.type === 'oauth2'
      ) as any;

      if (!oauth2Scheme) {
        return DEFAULT_OAUTH_CONFIG;
      }

      // Extract from authorizationCode flow
      const authCodeFlow = oauth2Scheme.flows?.authorizationCode;
      if (!authCodeFlow) {
        return DEFAULT_OAUTH_CONFIG;
      }

      const config: OAuthConfig = {
        clientId: authCodeFlow['x-clientId'] || null,
        scopes: authCodeFlow.scopes ? Object.keys(authCodeFlow.scopes) : []
      };

      return config;
    } catch (error) {
      console.error('Error extracting OAuth config:', error);
      return DEFAULT_OAUTH_CONFIG;
    }
  }

  /**
   * Gets the base server URL from the current spec
   * @returns Base server URL or empty string if not available
   */
  getBaseServerUrl(): string {
    const spec = this.currentState.swaggerSpec;
    if (!spec || !spec.servers || spec.servers.length === 0) {
      return '';
    }
    return spec.servers[0].url || '';
  }

  /**
   * Get subscription key for a specific server URL
   * @param targetUrl The target API URL to find subscription key for
   * @returns Subscription key or null if not found
   */
  getSubscriptionKeyForUrl(targetUrl: string): string | null {
    const spec = this.currentState.swaggerSpec;
    if (!spec || !spec.servers) {
      return null;
    }

    // Try exact URL match first
    for (const server of spec.servers) {
      if (server.url && targetUrl.includes(server.url)) {
        if (server['x-subscriptionKey']) {
          return server['x-subscriptionKey'];
        }
      }
    }

    // Try hostname match
    try {
      const targetUrlObj = new URL(targetUrl);
      for (const server of spec.servers) {
        if (server.url && server['x-subscriptionKey']) {
          const serverUrl = new URL(server.url);
          if (serverUrl.hostname === targetUrlObj.hostname) {
            return server['x-subscriptionKey'];
          }
        }
      }
    } catch (error) {
      console.warn('Error parsing URL for subscription key lookup:', error);
    }

    return null;
  }

  // ============================================================================
  // Pending Requests Management
  // ============================================================================

  /**
   * Add a pending request (waiting for response)
   * @param requestUrl Original request URL (used as key)
   * @param originalRequest Original request data
   * @param displayRequest Display-friendly request data
   * @param proxyUrl Proxied URL that was actually called
   */
  addPendingRequest(
    requestUrl: string,
    originalRequest: SwaggerRequest,
    displayRequest: SwaggerRequest,
    proxyUrl: string
  ): void {
    const pendingRequest: PendingRequest = {
      originalRequest,
      displayRequest,
      proxyUrl,
      timestamp: Date.now()
    };

    const currentState = this.currentState;
    const updatedRequests = new Map(currentState.pendingRequests);
    updatedRequests.set(requestUrl, pendingRequest);

    this.updateState({
      pendingRequests: updatedRequests,
      lastOriginalRequest: originalRequest,
      lastProxyUrl: proxyUrl,
      lastOriginalUrl: requestUrl
    });
  }

  /**
   * Get a pending request by URL
   * @param requestUrl Original request URL
   */
  getPendingRequest(requestUrl: string): PendingRequest | null {
    return this.currentState.pendingRequests.get(requestUrl) || null;
  }

  /**
   * Remove a pending request (after response received)
   * @param requestUrl Original request URL
   */
  removePendingRequest(requestUrl: string): boolean {
    const currentState = this.currentState;
    const updatedRequests = new Map(currentState.pendingRequests);
    const deleted = updatedRequests.delete(requestUrl);

    if (deleted) {
      this.updateState({ pendingRequests: updatedRequests });
    }

    return deleted;
  }

  /**
   * Clear all pending requests
   */
  clearPendingRequests(): void {
    this.updateState({ pendingRequests: new Map() });
  }

  /**
   * Get count of pending requests
   */
  getPendingRequestCount(): number {
    return this.currentState.pendingRequests.size;
  }

  /**
   * Clean up old pending requests (older than specified time)
   * @param maxAgeMs Maximum age in milliseconds (default: 5 minutes)
   */
  cleanupOldPendingRequests(maxAgeMs: number = 5 * 60 * 1000): number {
    const currentTime = Date.now();
    const currentState = this.currentState;
    const updatedRequests = new Map(currentState.pendingRequests);
    let removedCount = 0;

    for (const [url, request] of updatedRequests.entries()) {
      if (currentTime - request.timestamp > maxAgeMs) {
        updatedRequests.delete(url);
        removedCount++;
      }
    }

    if (removedCount > 0) {
      this.updateState({ pendingRequests: updatedRequests });
    }

    return removedCount;
  }

  // ============================================================================
  // Active Operations Management
  // ============================================================================

  /**
   * Add an active operation
   * @param operationId Unique operation identifier
   */
  addActiveOperation(operationId: string): void {
    const currentState = this.currentState;
    const updatedOperations = new Set(currentState.activeOperations);
    updatedOperations.add(operationId);
    this.updateState({ activeOperations: updatedOperations });
  }

  /**
   * Remove an active operation
   * @param operationId Unique operation identifier
   */
  removeActiveOperation(operationId: string): boolean {
    const currentState = this.currentState;
    const updatedOperations = new Set(currentState.activeOperations);
    const deleted = updatedOperations.delete(operationId);

    if (deleted) {
      this.updateState({ activeOperations: updatedOperations });
    }

    return deleted;
  }

  /**
   * Clear all active operations
   */
  clearActiveOperations(): void {
    this.updateState({ activeOperations: new Set() });
  }

  /**
   * Check if operation is active
   */
  isOperationActive(operationId: string): boolean {
    return this.currentState.activeOperations.has(operationId);
  }

  // ============================================================================
  // Backward Compatibility (Legacy Window Object Support)
  // ============================================================================

  /**
   * Get last original request (for backward compatibility)
   */
  getLastOriginalRequest(): SwaggerRequest | null {
    return this.currentState.lastOriginalRequest;
  }

  /**
   * Get last display request (for UI display purposes)
   * Returns the display-friendly version of the request with proper headers for curl/UI display
   */
  getLastDisplayRequest(): SwaggerRequest | null {
    const lastOriginalUrl = this.currentState.lastOriginalUrl;
    if (!lastOriginalUrl) {
      return null;
    }

    const pendingRequest = this.getPendingRequest(lastOriginalUrl);
    return pendingRequest?.displayRequest || null;
  }

  /**
   * Get last proxy URL (for backward compatibility)
   */
  getLastProxyUrl(): string | null {
    return this.currentState.lastProxyUrl;
  }

  /**
   * Get last original URL (for backward compatibility)
   */
  getLastOriginalUrl(): string | null {
    return this.currentState.lastOriginalUrl;
  }

  // ============================================================================
  // State Management Utilities
  // ============================================================================

  /**
   * Reset state to initial values
   */
  reset(): void {
    this.stateSubject.next(this.createInitialState());
  }

  /**
   * Complete cleanup - call on service destroy or component unmount
   */
  cleanup(): void {
    this.clearPendingRequests();
    this.clearActiveOperations();
    this.cleanupOldPendingRequests(0); // Remove all
  }

  /**
   * Get state snapshot for debugging
   */
  getStateSnapshot(): {
    hasAuthToken: boolean;
    hasSwaggerSpec: boolean;
    pendingRequestsCount: number;
    activeOperationsCount: number;
  } {
    const state = this.currentState;
    return {
      hasAuthToken: !!state.authToken,
      hasSwaggerSpec: !!state.swaggerSpec,
      pendingRequestsCount: state.pendingRequests.size,
      activeOperationsCount: state.activeOperations.size
    };
  }

  // ============================================================================
  // Private Methods
  // ============================================================================

  /**
   * Create initial state with proper object creation
   */
  private createInitialState(): SwaggerState {
    return {
      ...DEFAULT_SWAGGER_STATE,
      pendingRequests: new Map(),
      activeOperations: new Set()
    };
  }

  /**
   * Update state immutably
   * @param partialState Partial state to merge
   */
  private updateState(partialState: Partial<SwaggerState>): void {
    const currentState = this.stateSubject.getValue();
    const newState: SwaggerState = {
      ...currentState,
      ...partialState
    };
    this.stateSubject.next(newState);
  }

  /**
   * Cleanup on service destroy
   */
  ngOnDestroy(): void {
    this.cleanup();
    this.destroy$.next();
    this.destroy$.complete();
    this.stateSubject.complete();
  }
}
